import { fileHref } from '../lib/fileAccess'
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useAuth } from '../hooks/useAuth'
import { fetchRows, createRow, updateRow } from '../lib/api'
import { supabase } from '../lib/supabase'
import { deleteWithUndo } from '../lib/undoDelete'
import { shipTodayAuto } from '../lib/shipTime'
import { assessorLabel } from '../lib/raStandard'

const CATEGORIES = ['Cybersecurity', 'Regulation', 'Safety', 'Immigration', 'Operational', 'General']
const PRIORITIES = ['Normal', 'Important', 'Urgent']

const sel = v => (v && typeof v === 'object' ? v.value : v) || ''
const today = () => new Date().toISOString().split('T')[0]
const priPill = p => (p === 'Urgent' ? 'pill-r' : p === 'Important' ? 'pill-a' : 'pill-x')
function parseAtt(s) { try { const a = JSON.parse(s || '[]'); return Array.isArray(a) ? a : [] } catch { return [] } }

export default function Announcements() {
  const { can } = useAuth()
  const { config } = useAuth()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [modal, setModal] = useState(null)
  const [saving, setSaving] = useState(false)
  const [aiText, setAiText] = useState('')
  const [drafting, setDrafting] = useState(false)
  const [fetching, setFetching] = useState(false)
  const [pack, setPack] = useState(null)
  const [tab, setTab] = useState('all')
  const [packing, setPacking] = useState(false)
  const [savingCl, setSavingCl] = useState(false)
  const [savingRa, setSavingRa] = useState(false)
  const [savingPack, setSavingPack] = useState(false)

  useEffect(() => { load() }, [])

  useEffect(() => {
    const raw = localStorage.getItem('hc_draft_article')
    if (!raw) return
    localStorage.removeItem('hc_draft_article')
    try {
      const a = JSON.parse(raw)
      const text = [a.title, a.summary].filter(Boolean).join('\n')
      setModal({ Title: '', Body: '', Category: 'General', Priority: 'Normal', 'Posted by': '', 'Posted date': today(), 'Source URL': a.link || '', Attachment: '', Active: true })
      setAiText(text)
      draft(text, a.link || '')
    } catch (e) {}
  }, [])

  async function load() {
    setLoading(true); setError('')
    try { setRows(await fetchRows('announcements')) }
    catch (e) { setError(e.message) }
    finally { setLoading(false) }
  }

  function openNew() {
    setModal({ Title: '', Body: '', Category: 'General', Priority: 'Normal', 'Posted by': '', 'Posted date': today(), 'Source URL': '', Attachment: '', Active: true })
  }
  function openEdit(r) {
    const att = parseAtt(r['Attachments'])
    setModal({
      id: r.id, Title: r['Title'] || '', Body: r['Body'] || '',
      Category: sel(r['Category']) || 'General', Priority: sel(r['Priority']) || 'Normal',
      'Posted by': r['Posted by'] || '', 'Posted date': r['Posted date'] || today(),
      'Source URL': r['Source URL'] || '', Attachment: att[0] || '', Active: r['Active'] !== false,
    })
  }

  async function fetchSource() {
    const url = (modal && modal['Source URL'] || '').trim()
    if (!url) { alert('Add a Source URL first, then fetch its full text.'); return }
    setFetching(true)
    try {
      const s = await (await import('../lib/supabase')).supabase.auth.getSession()
      const hdr = s.data.session ? { Authorization: 'Bearer ' + s.data.session.access_token } : {}
      const r = await fetch('/api/ai', {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...hdr },
        body: JSON.stringify({ action: 'fetch', url }),
      })
      const d = await r.json()
      if (!r.ok) throw new Error(d.error || 'Fetch failed')
      setAiText(d.text)
    } catch (e) { alert('Could not read source: ' + e.message) }
    finally { setFetching(false) }
  }

  async function draft(overrideText, overrideSource) {
    const text = ((overrideText != null ? overrideText : aiText) || '').trim()
    if (!text) { alert('Paste an article, notice, or a few lines first.'); return }
    setDrafting(true)
    try {
      const r = await fetch('/api/ai', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await (await import('../lib/supabase')).supabase.auth.getSession().then(s => s.data.session ? { Authorization: 'Bearer ' + s.data.session.access_token } : {})) },
        body: JSON.stringify({ action: 'draft', title: '', summary: text, source: (overrideSource != null ? overrideSource : (modal && modal['Source URL'])) || '' }),
      })
      const d = await r.json()
      if (!r.ok) throw new Error(d.error || 'Draft failed')
      setModal(m => ({ ...m, Title: d.title, Body: d.body, Category: d.category, Priority: d.priority }))
    } catch (e) { alert('AI draft failed: ' + e.message) }
    finally { setDrafting(false) }
  }

  async function save() {
    if (!modal.Title.trim()) { alert('Title is required'); return }
    setSaving(true)
    try {
      const body = {
        Title: modal.Title.trim(), Body: modal.Body || '',
        Category: modal.Category || 'General', Priority: modal.Priority || 'Normal',
        'Posted by': modal['Posted by'] || '', 'Posted date': modal['Posted date'] || today(),
        Active: !!modal.Active,
      }
      if (modal['Source URL'] && modal['Source URL'].trim()) body['Source URL'] = modal['Source URL'].trim()
      const att = modal.Attachment && modal.Attachment.trim() ? [modal.Attachment.trim()] : []
      body['Attachments'] = JSON.stringify(att)
      if (modal.id) await updateRow('announcements', modal.id, body)
      else await createRow('announcements', body)
      setModal(null); await load()
    } catch (e) { alert('Save failed: ' + e.message) }
    finally { setSaving(false) }
  }

  async function remove(r) {
    const label = r['Title'] || 'bulletin'
    if (!window.confirm('Delete the bulletin "' + label + '"? You can undo it for 10 seconds.')) return
    try { await deleteWithUndo(supabase, { table: 'announcements', id: r.id, label: 'bulletin "' + label + '"', onUndone: () => load() }); await load() }
    catch (e) { alert('Delete failed: ' + e.message) }
  }
  async function toggleActive(r) {
    try { await updateRow('announcements', r.id, { Active: !(r['Active'] !== false) }); await load() }
    catch (e) { alert(e.message) }
  }

  async function compliancePack(r) {
    setPacking(true)
    setPack({ row: r, summary: '', key_points: [], action_items: [], risk_assessment: [], loading: true })
    try {
      const s = await (await import('../lib/supabase')).supabase.auth.getSession()
      const hdr = s.data.session ? { Authorization: 'Bearer ' + s.data.session.access_token } : {}
      let text = r['Body'] || ''
      let pdfUrl = null
      const link = r['Source URL']
      if (link) {
        try {
          const fr = await fetch('/api/ai', { method: 'POST', headers: { 'Content-Type': 'application/json', ...hdr }, body: JSON.stringify({ action: 'fetch', url: link }) })
          const fd = await fr.json()
          if (fr.ok) { if (fd.text) text = fd.text; if (fd.pdf_url) pdfUrl = fd.pdf_url }
        } catch (e) {}
      }
      const rr = await fetch('/api/ai', {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...hdr },
        body: JSON.stringify({ action: 'risk', text, title: r['Title'] || '', vessel: (config && config.vessel_name) || '', source_url: link || '', pdf_url: pdfUrl }),
      })
      const d = await rr.json()
      if (!rr.ok) throw new Error(d.error || 'Failed')
      setPack({ row: r, summary: d.summary || '', key_points: d.key_points || [], whats_changed: d.whats_changed || [], procedure: d.procedure || [], action_items: d.action_items || [], risk_assessment: d.risk_assessment || [], loading: false })

      // Cheap text-only enrich pass to fill procedure + whats_changed.
      if ((!d.procedure || !d.procedure.length) || (!d.whats_changed || !d.whats_changed.length)) {
        try {
          const er = await fetch('/api/ai', {
            method: 'POST', headers: { 'Content-Type': 'application/json', ...hdr },
            body: JSON.stringify({ action: 'enrich', summary: d.summary || '', key_points: d.key_points || [], action_items: d.action_items || [] }),
          })
          const ed = await er.json()
          if (er.ok) setPack(prev => prev ? ({ ...prev,
            procedure: (prev.procedure && prev.procedure.length) ? prev.procedure : (ed.procedure || []),
            whats_changed: (prev.whats_changed && prev.whats_changed.length) ? prev.whats_changed : (ed.whats_changed || []),
          }) : prev)
        } catch (e) {}
      }
    } catch (e) {
      alert('Compliance pack failed: ' + e.message)
      setPack(null)
    } finally { setPacking(false) }
  }

  async function savePack() {
    if (!pack || !pack.row) return
    setSavingPack(true)
    try {
      const payload = {
        summary: pack.summary, key_points: pack.key_points || [],
        whats_changed: pack.whats_changed || [], procedure: pack.procedure || [],
        action_items: pack.action_items || [], risk_assessment: pack.risk_assessment || [],
        generated_at: new Date().toISOString(),
      }
      await updateRow('announcements', pack.row.id, { 'Compliance Pack': JSON.stringify(payload) })
      alert('Compliance pack saved to this bulletin.')
      setPack(p => ({ ...p, saved: true }))
      await load()
    } catch (e) { alert('Could not save pack: ' + e.message) }
    finally { setSavingPack(false) }
  }

  function viewPack(r) {
    try {
      const d = JSON.parse(r['Compliance Pack'])
      setPack({ row: r, summary: d.summary || '', key_points: d.key_points || [], whats_changed: d.whats_changed || [], procedure: d.procedure || [], action_items: d.action_items || [], risk_assessment: d.risk_assessment || [], loading: false, saved: true })
    } catch (e) { alert('Saved pack could not be read; regenerate it.') }
  }

  async function createRiskAssessment() {
    if (!pack || !pack.row) return
    setSavingRa(true)
    try {
      const s = await (await import('../lib/supabase')).supabase.auth.getSession()
      const hdr = s.data.session ? { Authorization: 'Bearer ' + s.data.session.access_token } : {}
      const notice = [
        pack.summary || '',
        (pack.key_points || []).join('\n'),
        (pack.action_items || []).map(a => (a.department ? a.department + ': ' : '') + a.task).join('\n'),
        (pack.risk_assessment || []).map(r => r.hazard + ' - ' + (r.further_action || '')).join('\n'),
      ].filter(Boolean).join('\n\n')

      const r = await fetch('/api/ai', {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...hdr },
        body: JSON.stringify({ action: 'risk_assessment', vessel: (config && config.vessel_name) || '', notice_text: notice }),
      })
      const d = await r.json()
      if (!r.ok) throw new Error(d.error || 'Generation failed')

      const { supabase } = await import('../lib/supabase')
      const { error: raErr } = await supabase.from('risk_assessments').insert({
        client_id: config.id,
        activity: d.activity || ('Risk assessment: ' + (pack.row['Title'] || '')),
        location: d.location || '',
        personnel: d.personnel || '',
        date_of_assessment: shipTodayAuto(),   // the ship's day, not UTC
        assessor: assessorLabel(await supabase.rpc('ra_reviewer_name', { p_client: config.id }).then(r => r.data, () => ''), config),
        other_ras: d.other_ras || '',
        status: 'Draft',
        hazards: d.hazards || [],
      })
      if (raErr) throw new Error(raErr.message)
      alert('Risk assessment created from this notice.\nFind it under Risk Assessments (Draft).')
    } catch (e) {
      alert('Could not create risk assessment: ' + e.message)
    } finally { setSavingRa(false) }
  }

  async function createComplianceChecklist() {
    if (!pack || !pack.action_items.length) return
    setSavingCl(true)
    try {
      const { supabase } = await import('../lib/supabase')
      const title = 'Comply with: ' + (pack.row['Title'] || 'Regulation')
      const { data: cl, error: clErr } = await supabase
        .from('checklists')
        .insert({ client_id: config.id, title: title.slice(0, 200), description: [pack.summary, (pack.key_points || []).map(k => '\u2022 ' + k).join('\n')].filter(Boolean).join('\n\n').slice(0, 2000), source: 'compliance', created_by: (config && config.vessel_name) || 'crew' })
        .select().single()
      if (clErr) throw new Error(clErr.message)
      const steps = pack.action_items.map((a, i) => ({
        checklist_id: cl.id,
        step_order: i + 1,
        title: (a.department ? a.department + ': ' : '') + a.task + (a.due_hint ? ' (' + a.due_hint + ')' : ''),
        required: true,
      }))
      const { error: itErr } = await supabase.from('checklist_items').insert(steps)
      if (itErr) throw new Error(itErr.message)
      alert('Checklist created: "' + title + '"\nFind it under Checklists.')
      setPack(null)
    } catch (e) {
      alert('Could not create checklist: ' + e.message)
    } finally { setSavingCl(false) }
  }

  if (loading) return <div className="loading-row"><div className="spin" />Loading bulletins...</div>
  if (error) return <div className="err-box">{error}</div>

  function catOf(r) {
    const c = (sel(r['Category']) || 'General')
    const by = (r['Posted by'] || '').toLowerCase()
    const title = (r['Title'] || '').toLowerCase()
    if (c === 'Weather' || by === 'weather' || title.startsWith('weather briefing')) return 'weather'
    if (c === 'Tides' || by === 'tides' || title.startsWith('tides \u2014') || title.startsWith('tides -')) return 'tides'
    if (r['Source URL']) return 'news'
    return 'notices'
  }

  const sorted = [...rows].sort((a, b) => (Date.parse(b['Posted date']) || 0) - (Date.parse(a['Posted date']) || 0))
  const visible = tab === 'all' ? sorted : sorted.filter(r => catOf(r) === tab)
  const TABS = [
    { id: 'all', label: 'All' },
    { id: 'notices', label: 'Notices' },
    { id: 'weather', label: 'Weather' },
    { id: 'tides', label: 'Tides' },
    { id: 'news', label: 'News' },
  ]

  return (
    <div className="fade-up">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <span style={{ fontSize: 11, color: 'var(--muted)' }}>{rows.length} bulletin{rows.length !== 1 ? 's' : ''}</span>
        {(!can || can('announcements','write')) && <button className="btn btn-primary" onClick={openNew}>+ New bulletin</button>}
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16, borderBottom: '0.5px solid var(--border)' }}>
        {TABS.map(t => {
          const n = t.id === 'all' ? sorted.length : sorted.filter(r => catOf(r) === t.id).length
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 600, padding: '8px 14px', color: tab === t.id ? 'var(--gold2)' : 'var(--muted)', borderBottom: tab === t.id ? '2px solid var(--gold2)' : '2px solid transparent', marginBottom: -1 }}>
              {t.label} <span style={{ opacity: .6, fontSize: 11 }}>{n}</span>
            </button>
          )
        })}
      </div>

      {visible.length === 0 && <div style={st.empty}>No posts in this section yet.</div>}

      {visible.map(r => {
        const active = r['Active'] !== false
        const att = parseAtt(r['Attachments'])
        const pri = sel(r['Priority']) || 'Normal'
        const cat = sel(r['Category']) || 'General'
        const src = r['Source URL']
        return (
          <div key={r.id} style={{ ...st.card, opacity: active ? 1 : 0.5 }}>
            <div style={st.cardTop}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span className={'pill ' + priPill(pri)}>{pri}</span>
                <span style={st.catTag}>{cat}</span>
                {!active && <span className="pill pill-x">Unpublished</span>}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                {r['Compliance Pack'] ? (
                  <button className="btn" style={{ ...st.sbtn, color: 'var(--gold2)' }} onClick={() => viewPack(r)}>View pack</button>
                ) : ['Regulation', 'Safety'].includes(cat) ? (
                  (!can || can('announcements','write')) && <button className="btn" style={{ ...st.sbtn, color: 'var(--gold2)' }} onClick={() => compliancePack(r)} disabled={packing}>Compliance pack</button>
                ) : null}
                {(!can || can('announcements','write')) && <>
                <button className="btn" style={st.sbtn} onClick={() => toggleActive(r)}>{active ? 'Unpublish' : 'Publish'}</button>
                <button className="btn" style={st.sbtn} onClick={() => openEdit(r)}>Edit</button>
                <button className="btn" style={st.sbtn} onClick={() => remove(r)}>Delete</button>
                </>}
              </div>
            </div>
            <div style={st.title}>{r['Title']}</div>
            {r['Body'] && <div style={st.body}>{r['Body']}</div>}
            {att.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 8 }}>
                {att.map((u, i) => <a key={i} href={fileHref(u)} target="_blank" rel="noopener noreferrer" style={st.link}>View attachment{att.length > 1 ? ' ' + (i + 1) : ''}</a>)}
              </div>
            )}
            <div style={st.meta}>
              {(r['Posted by'] || '\u2014')}{r['Posted date'] ? ' \u00b7 ' + r['Posted date'] : ''}
              {src ? <> {'\u00b7'} <a href={src} target="_blank" rel="noopener noreferrer" style={st.link}>Source</a></> : null}
            </div>
          </div>
        )
      })}

      {pack && createPortal((
        <div style={st.overlay} onClick={() => !pack.loading && setPack(null)}>
          <div style={st.modal} onClick={e => e.stopPropagation()}>
            <div style={st.modalTitle}>Compliance pack</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 12 }}>{pack.row && pack.row['Title']}</div>
            {pack.loading ? (
              <div className="loading-row"><div className="spin" />Reading source & generating...</div>
            ) : (
              <>
                {pack.summary && <div style={{ ...st.body, marginBottom: 12 }}>{pack.summary}</div>}

                {pack.key_points && pack.key_points.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={st.packHd}>Key points</div>
                    {pack.key_points.map((k, i) => (
                      <div key={i} style={{ display: 'flex', gap: 8, padding: '4px 0', fontSize: 12.5, color: 'var(--text)' }}>
                        <span style={{ color: 'var(--gold2)', flexShrink: 0 }}>&bull;</span>
                        <span>{k}</span>
                      </div>
                    ))}
                  </div>
                )}
                {pack.whats_changed && pack.whats_changed.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={st.packHd}>What changed</div>
                    {pack.whats_changed.map((c, i) => (
                      <div key={i} style={{ display: 'flex', gap: 8, padding: '4px 0', fontSize: 12.5, color: 'var(--text)' }}>
                        <span style={{ color: '#137050', flexShrink: 0 }}>&#9656;</span>
                        <span>{c}</span>
                      </div>
                    ))}
                  </div>
                )}

                <div style={st.packHd}>Action items ({pack.action_items.length})</div>
                {pack.action_items.map((a, i) => (
                  <div key={i} style={st.actionRow}>
                    <span style={st.deptTag}>{a.department}</span>
                    <span style={{ flex: 1, fontSize: 12.5, color: 'var(--text)' }}>{a.task}{a.due_hint ? <span style={{ color: 'var(--muted)' }}> — {a.due_hint}</span> : null}</span>
                  </div>
                ))}
                {pack.procedure && pack.procedure.length > 0 && (
                  <div style={{ marginTop: 18 }}>
                    <div style={st.packHd}>Procedure</div>
                    {pack.procedure.map((step, i) => (
                      <div key={i} style={{ display: 'flex', gap: 8, padding: '5px 0', fontSize: 12.5, color: 'var(--text)', borderBottom: '0.5px solid rgba(255,255,255,.04)' }}>
                        <span style={{ color: 'var(--gold2)', flexShrink: 0, fontWeight: 600, minWidth: 18 }}>{i + 1}.</span>
                        <span>{step}</span>
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ ...st.packHd, marginTop: 18 }}>Risk assessment ({pack.risk_assessment.length})</div>
                {pack.risk_assessment.map((rk, i) => (
                  <div key={i} style={st.riskCard}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                      <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)' }}>{rk.hazard}</span>
                      <span className={'pill ' + (rk.residual_risk === 'High' ? 'pill-r' : rk.residual_risk === 'Medium' ? 'pill-a' : 'pill-g')}>{rk.residual_risk}</span>
                    </div>
                    <div style={st.riskLine}><b>At risk:</b> {rk.persons_at_risk}</div>
                    <div style={st.riskLine}><b>Existing controls:</b> {rk.existing_controls}</div>
                    <div style={st.riskLine}><b>Further action:</b> {rk.further_action}</div>
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
                  <button className="btn" onClick={() => setPack(null)}>Close</button>
                  {!pack.saved && (
                    <button className="btn" onClick={savePack} disabled={savingPack}>{savingPack ? 'Saving...' : 'Save pack'}</button>
                  )}
                  <button className="btn" onClick={createRiskAssessment} disabled={savingRa}>{savingRa ? 'Creating...' : 'Create risk assessment'}</button>
                  {pack.action_items.length > 0 && (
                    <button className="btn btn-primary" onClick={createComplianceChecklist} disabled={savingCl}>{savingCl ? 'Creating...' : 'Create checklist'}</button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      ), document.body)}

      {modal && createPortal((
        <div style={st.overlay} onClick={() => !saving && setModal(null)}>
          <div style={st.modal} onClick={e => e.stopPropagation()}>
            <div style={st.modalTitle}>{modal.id ? 'Edit bulletin' : 'New bulletin'}</div>

            <div style={st.aiBox}>
              <label style={{ ...st.lbl, marginTop: 0 }}>Draft with AI — paste an article, notice, or a few lines</label>
              <textarea style={{ ...st.input, minHeight: 60, resize: 'vertical' }} value={aiText} onChange={e => setAiText(e.target.value)} placeholder="Paste a news article or regulation here, then click Draft..." />
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button className="btn" style={{ fontSize: 12 }} onClick={fetchSource} disabled={fetching}>{fetching ? 'Reading...' : 'Read full source'}</button>
                <button className="btn" style={{ fontSize: 12 }} onClick={() => draft()} disabled={drafting}>{drafting ? 'Drafting...' : 'Draft with AI'}</button>
              </div>
            </div>

            <label style={st.lbl}>Title *</label>
            <input style={st.input} value={modal.Title} onChange={e => setModal({ ...modal, Title: e.target.value })} placeholder="e.g. Cybersecurity bulletin - fraudulent calls" />

            <label style={st.lbl}>Body</label>
            <textarea style={{ ...st.input, minHeight: 120, resize: 'vertical' }} value={modal.Body} onChange={e => setModal({ ...modal, Body: e.target.value })} placeholder="What changed, what the crew must do, by when..." />

            <div style={st.row2}>
              <div style={{ flex: 1 }}>
                <label style={st.lbl}>Category</label>
                <select style={st.input} value={modal.Category} onChange={e => setModal({ ...modal, Category: e.target.value })}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={st.lbl}>Priority</label>
                <select style={st.input} value={modal.Priority} onChange={e => setModal({ ...modal, Priority: e.target.value })}>
                  {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            </div>

            <div style={st.row2}>
              <div style={{ flex: 1 }}>
                <label style={st.lbl}>Posted by</label>
                <input style={st.input} value={modal['Posted by']} onChange={e => setModal({ ...modal, 'Posted by': e.target.value })} placeholder="Your name" />
              </div>
              <div style={{ flex: 1 }}>
                <label style={st.lbl}>Posted date</label>
                <input type="date" style={st.input} value={modal['Posted date']} onChange={e => setModal({ ...modal, 'Posted date': e.target.value })} />
              </div>
            </div>

            <label style={st.lbl}>Source URL (optional)</label>
            <input style={st.input} value={modal['Source URL']} onChange={e => setModal({ ...modal, 'Source URL': e.target.value })} placeholder="https://..." />

            <label style={st.lbl}>Attachment URL (optional)</label>
            <input style={st.input} value={modal.Attachment} onChange={e => setModal({ ...modal, Attachment: e.target.value })} placeholder="Link to a PDF" />

            <label style={{ ...st.lbl, display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, cursor: 'pointer' }}>
              <input type="checkbox" checked={!!modal.Active} onChange={e => setModal({ ...modal, Active: e.target.checked })} />
              Published (visible on board)
            </label>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
              <button className="btn" onClick={() => setModal(null)} disabled={saving}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving...' : (modal.id ? 'Save' : 'Post bulletin')}</button>
            </div>
          </div>
        </div>
      ), document.body)}
    </div>
  )
}

const st = {
  card: { background: 'var(--hull)', border: '0.5px solid var(--border)', borderRadius: 'var(--radius)', padding: '14px 16px', marginBottom: 10 },
  cardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 8 },
  catTag: { fontSize: 10, color: 'var(--muted)', border: '0.5px solid var(--border)', borderRadius: 6, padding: '2px 7px', letterSpacing: '.04em' },
  title: { fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 5, lineHeight: 1.3 },
  body: { fontSize: 12.5, color: 'var(--text)', opacity: .85, lineHeight: 1.5, whiteSpace: 'pre-wrap' },
  meta: { fontSize: 10.5, color: 'var(--muted)', marginTop: 10 },
  link: { color: 'var(--gold2)', textDecoration: 'none' },
  sbtn: { fontSize: 11, padding: '3px 9px' },
  empty: { fontSize: 12, color: 'var(--muted)', textAlign: 'center', padding: '40px 0' },
  overlay: { position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 16px', boxSizing: 'border-box', zIndex: 1000 },
  modal: { background: 'var(--ink2)', border: '0.5px solid var(--border)', borderRadius: 12, padding: '22px 24px', width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto', boxSizing: 'border-box' },
  modalTitle: { fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 6 },
  lbl: { fontSize: 11, color: 'var(--muted)', display: 'block', marginBottom: 5, marginTop: 12 },
  input: { width: '100%', background: 'var(--hull)', border: '0.5px solid var(--border)', borderRadius: 8, padding: '9px 11px', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' },
  row2: { display: 'flex', gap: 12 },
  packHd: { fontSize: 10, color: 'var(--muted)', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 8, borderBottom: '0.5px solid var(--border)', paddingBottom: 5 },
  actionRow: { display: 'flex', alignItems: 'flex-start', gap: 8, padding: '6px 0', borderBottom: '0.5px solid rgba(255,255,255,.04)' },
  deptTag: { fontSize: 9, color: 'var(--gold2)', border: '0.5px solid var(--border)', borderRadius: 5, padding: '2px 7px', whiteSpace: 'nowrap', flexShrink: 0 },
  riskCard: { background: 'var(--hull)', border: '0.5px solid var(--border)', borderRadius: 8, padding: '10px 12px', marginBottom: 8 },
  riskLine: { fontSize: 11.5, color: 'var(--text)', opacity: .85, marginTop: 4, lineHeight: 1.4 },
  aiBox: { background: 'rgba(184,150,62,.06)', border: '0.5px solid var(--border)', borderRadius: 8, padding: '12px 14px', marginTop: 14, marginBottom: 4 },
}
